import type { Numeric } from '@/utils/numeric';

/**
 * Pest & disease forecasting types. Mirrors the /api/pest-disease backend
 * (pestService / pestDiseaseService / pestAlertService).
 */

export type PestModelType = 'degree_day' | 'infection_wetness' | 'generic_infection' | 'risk_index';
export type PestKind = 'disease' | 'insect' | 'weed';
export type RiskLevel = 'none' | 'low' | 'moderate' | 'high' | 'severe';
export type WeatherSource = 'station' | 'open_meteo' | 'mixed' | null;

// ─── model_params archetype shapes ───────────────────────────────────────────

export interface SeasonCfg {
  start: { month: number; day: number };
  end: { month: number; day: number };
}

export interface DegreeDayEvent {
  name: string;
  dd: number;
  risk?: RiskLevel;
  action?: string;
}

export interface DegreeDayParams {
  base_temp_c: number;
  upper_temp_c?: number;
  method?: string;
  biofix_fallback?: { month: number; day: number };
  season?: SeasonCfg;
  events: DegreeDayEvent[];
}

export interface MillsRow {
  t: number;
  light: number;
  moderate: number;
  severe: number;
}

export interface InfectionParams {
  wetness_source?: 'auto' | 'sensor';
  rh_threshold_pct?: number;
  min_temp_c?: number;
  max_temp_c?: number;
  dry_break_hours?: number;
  leaf_wetness_threshold?: number;
  season?: SeasonCfg;
  mills_table: MillsRow[];
}

/** Magarey et al. (2005) generic infection model — cardinal values. */
export interface GenericInfectionParams {
  t_min_c: number;
  t_opt_c: number;
  t_max_c: number;
  w_min_h: number;
  w_max_h?: number;
  dry_break_hours?: number;
  rh_threshold_pct?: number;
  wetness_source?: 'auto' | 'sensor';
  season?: SeasonCfg;
}

export interface RiskIndexParams {
  opt_temp_min?: number;
  opt_temp_max?: number;
  rh_threshold_pct?: number;
  bands?: { low?: number; moderate?: number; high?: number; severe?: number };
  season?: SeasonCfg;
}

export type PestModelParams = DegreeDayParams | InfectionParams | GenericInfectionParams | RiskIndexParams | Record<string, unknown>;

// ─── researched profile ──────────────────────────────────────────────────────

/** What a grower needs to recognise and manage the pest. Display text only. */
export interface PestProfile {
  other_names?: string[];
  symptoms?: string[];
  lifecycle?: string | null;
  favourable_conditions?: string | null;
  monitoring?: string[];
  management?: { cultural?: string[]; biological?: string[]; chemical?: string[] };
  lookalikes?: string[];
  australian_status?: string | null;
  notifiable?: boolean;
  model_rationale?: string | null;
  sources?: Array<{ title: string; url: string }>;
  researched_at?: string;
  researched_by_model?: string;
}

/** POST /pest-disease/lookup/identify — what the grower meant. */
export interface PestIdentity {
  identified: true;
  confidence: 'high' | 'medium' | 'low';
  name: string;
  common_name: string | null;
  scientific_name: string | null;
  kind: PestKind;
  host_crop_type_ids: number[];
  hosts_not_listed: string[];
  warnings: string[];
  existing_match: { id: number; name: string } | null;
}

/** POST /pest-disease/lookup/model */
export interface PestModelResearch {
  model_type: PestModelType;
  model_params: PestModelParams;
  rationale: string | null;
  recommended_actions: string | null;
  warnings: string[];
}

/** POST /pest-disease/lookup/guide */
export interface PestGuideResearch {
  description: string | null;
  profile: PestProfile;
  warnings: string[];
}

/** The three steps combined — a proposal, never a saved record. */
export type PestLookupResult =
  | {
      identified: true;
      confidence: 'high' | 'medium' | 'low';
      name: string;
      common_name: string | null;
      scientific_name: string | null;
      kind: PestKind;
      description: string | null;
      recommended_actions: string | null;
      host_crop_type_ids: number[];
      hosts_not_listed: string[];
      model_type: PestModelType;
      model_params: PestModelParams;
      profile: PestProfile;
      warnings: string[];
      existing_match: { id: number; name: string } | null;
      web_searches_used?: number | null;
    }
  | { identified: false; message: string; candidates: string[] };

// ─── pest model ──────────────────────────────────────────────────────────────

export interface PestModel {
  id: number;
  organisation_id: number | null;
  name: string;
  common_name: string | null;
  scientific_name: string | null;
  kind: PestKind;
  description: string | null;
  model_type: PestModelType;
  model_params: PestModelParams;
  recommended_actions: string | null;
  profile: PestProfile | null;
  is_active: boolean;
  host_crop_type_ids: number[];
  host_crop_type_names?: string[];
  created_at: string;
  updated_at: string;
}

export interface PestModelInput {
  name: string;
  common_name?: string | null;
  scientific_name?: string | null;
  kind: PestKind;
  description?: string | null;
  model_type: PestModelType;
  model_params: PestModelParams;
  recommended_actions?: string | null;
  host_crop_type_ids: number[];
  is_active?: boolean;
  profile?: PestProfile | null;
}

export interface MatchedFarm {
  id: number;
  name: string;
  center_point: unknown;
  auto: boolean;
  activation_enabled: boolean | null;
}

// ─── risk series ─────────────────────────────────────────────────────────────

export interface PestRiskDay {
  local_date: string;
  value_day: Numeric;
  value_cum: Numeric;
  risk_score: Numeric;
  risk_level: RiskLevel;
  events: Array<{ name: string; action: string | null }>;
  hours_total: number;
  hours_station: number;
  hours_modelled: number;
  source: WeatherSource;
  is_forecast: boolean;
  /** Rain that day (mm); null on a series computed before rain was kept. */
  rain_mm?: number | null;
}

export interface PestRiskMeta {
  pest_model_id: number;
  pest_name: string;
  model_type: PestModelType;
  season_year: number;
  season_start: string;
  season_end: string;
  through_date: string;
  forecast_end?: string;
  timezone: string;
  source: string;
  station_id: number | null;
  station_name: string | null;
  farm_id: number;
  farm_name: string;
  biofix_date: string | null;
}

export interface PestNextEvent {
  name: string;
  dd: number;
  action: string | null;
  projected_date: string | null;
}

export interface PestRiskSummary {
  current_level: RiskLevel;
  current_value: Numeric;
  days_total: number;
  last_date: string | null;
  peak_forecast_level: RiskLevel;
  peak_forecast_date: string | null;
  next_event: PestNextEvent | null;
}

export interface PestRiskSeries {
  meta: PestRiskMeta;
  summary: PestRiskSummary;
  series: PestRiskDay[];
  cached: boolean;
}

export interface PestOverviewItem {
  pest_model_id: number;
  pest_name: string;
  kind: PestKind;
  model_type: PestModelType;
  farm_id: number;
  farm_name: string;
  summary: PestRiskSummary | null;
}

export interface PestBiofix {
  id: number;
  pest_model_id: number;
  farm_id: number;
  season_year: number;
  biofix_date: string;
  source: 'manual' | 'trap' | 'phenology';
  note: string | null;
}

// ─── alert rules ─────────────────────────────────────────────────────────────

export type PestConditionMetric = 'value_cum' | 'value_day' | 'risk_score' | 'risk_level' | 'rain_mm' | 'rain_3d_mm';
export type ConditionOp = 'lt' | 'lte' | 'gt' | 'gte' | 'eq' | 'neq';

export interface PestLeafCondition {
  metric: PestConditionMetric;
  op: ConditionOp;
  value: number | string;
}
export interface PestGroupCondition {
  all?: PestLeafCondition[];
  any?: PestLeafCondition[];
}
export type PestCondition = PestLeafCondition | PestGroupCondition;

export type PestRecipientSpec = number[] | 'all_admins' | 'spray_admins';

export interface PestNotifyAction {
  type: 'notify_in_app' | 'notify_email';
  user_ids: PestRecipientSpec;
  title?: string;
  subject?: string;
  body?: string;
}

/** 'risk' fires on the daily series; 'protection' on wash-off / curative-window events. */
export type PestAlertTrigger = 'risk' | 'protection';
export type ProtectionEventType = 'curative_window' | 'forecast_unprotected' | 'washed_off' | 'washoff_forecast';
export const PROTECTION_EVENT_TYPES: ProtectionEventType[] = ['curative_window', 'forecast_unprotected', 'washed_off', 'washoff_forecast'];

export interface PestAlertRule {
  id: number;
  organisation_id: number;
  pest_model_id: number;
  farm_id: number | null;
  name: string;
  enabled: boolean;
  trigger_type: PestAlertTrigger;
  protection_events: ProtectionEventType[] | null;
  condition: PestCondition | null;
  lead_time_days: number;
  actions: PestNotifyAction[];
  cooldown_sec: number;
  hysteresis: PestCondition | null;
  last_fired_at: string | null;
  last_error: string | null;
  created_at: string;
  updated_at: string;
}

export interface PestAlertRuleInput {
  pest_model_id: number;
  farm_id?: number | null;
  name: string;
  enabled?: boolean;
  trigger_type?: PestAlertTrigger;
  protection_events?: ProtectionEventType[] | null;
  condition: PestCondition | null;
  lead_time_days?: number;
  actions: PestNotifyAction[];
  cooldown_sec?: number;
  hysteresis?: PestCondition | null;
}

export interface PestAlertEvent {
  id: number;
  rule_id: number;
  pest_model_id: number | null;
  farm_id: number | null;
  fired_at: string;
  risk_level: RiskLevel | null;
  trigger_value: Numeric;
  forecast_date: string | null;
  actions_run: Array<{ type: string; ok: boolean; error?: string; detail?: unknown }>;
  note: string | null;
}

// ─── registered products + target-pest links ──────────────────────────────────

export type DiseaseActivity = 'protectant' | 'curative' | 'both';

/** A registration target pest this forecast pest is known as. */
export interface PestTargetLink {
  pest_key: string;
  label: string;
  apvma_pestcode: string | null;
}

/** GET /pest-disease/registered-pests — what the registrations actually name. */
export interface RegisteredPestOption {
  pest_key: string;
  label: string;
  apvma_pestcode: string | null;
  product_count: number;
}

/** fungicideActivity.resolveActivity on the backend. */
export interface ResolvedProtection {
  activity: DiseaseActivity | null;
  washoff_mm: number | null;
  kickback_hours: number | null;
  groups: string[];
  source: { activity: 'chemical' | 'group' | null; washoff: 'chemical' | 'default' | null; kickback: 'chemical' | 'group' | null };
}

export interface PestChemicalRegistration {
  id: number;
  situation_id: number | null;
  situation_name: string | null;
  target_pest: string | null;
  on_host_crop: boolean;
  min_rate_per_ha: Numeric;
  max_rate_per_ha: Numeric;
  rate_unit: string | null;
  rate_status: string | null;
  whp_days: number | null;
  rei_hours: number | null;
  max_applications_per_season: number | null;
  min_days_between_apps: number | null;
  growth_stage_restriction: string | null;
  source: string | null;
}

export interface PestChemical {
  chemical_id: number;
  product_name: string;
  trade_name: string | null;
  manufacturer: string | null;
  apvma_approval_number: string | null;
  mode_of_action_group: string | null;
  mode_of_action_type: string | null;
  registration_status: string | null;
  protection: ResolvedProtection | null;
  on_host_crops: boolean;
  registrations: PestChemicalRegistration[];
}

export interface PestChemicalsResult {
  target_pests: PestTargetLink[];
  host_limited: boolean;
  host_situation_count: number;
  other_crop_product_count?: number;
  chemicals: PestChemical[];
}

// ─── protection picture ──────────────────────────────────────────────────────

export type ProtectionState = 'protected' | 'washed_off' | 'unprotected';
export type InfectionStatus = 'protected' | 'cured' | 'curative_window' | 'missed' | 'forecast_protected' | 'forecast_unprotected';

export interface KickbackWindow {
  key: string;
  label: string;
  kickback_hours: number;
  products: string[];
  closes_at: number;
  open: boolean;
}

export interface ProtectionInfection {
  start: number;
  end: number;
  severity: 'light' | 'moderate' | 'severe';
  is_forecast: boolean;
  status: InfectionStatus;
  protected_by: { spray_id: number; spray_name: string } | null;
  cured_by: { spray_id: number; spray_name: string; applied_at: number; products: string[] } | null;
  windows: KickbackWindow[];
}

export interface LastProtectant {
  spray_id: number;
  spray_name: string;
  applied_at: number;
  products: string[];
  washoff_mm: number;
  washoff_source: 'chemical' | 'default';
  rain_since_mm: number;
  remaining_mm: number;
  rain_with_forecast_mm: number;
  washoff_at: number | null;
  washoff_forecast: boolean;
}

export type ProtectionAction =
  | { kind: 'none' }
  | { kind: 'curative_now'; infection_start: number; closes_at: number }
  | { kind: 'protect_before'; infection_start: number }
  | { kind: 'reprotect' }
  | { kind: 'washoff_forecast'; washoff_at: number };

export interface ProtectionUnit {
  cultivar_area_id: number;
  name: string;
  block_id: number | null;
  block_name: string | null;
  cultivar_name: string | null;
  state: ProtectionState;
  unprotected_since: number | null;
  last_protectant: LastProtectant | null;
  infections: ProtectionInfection[];
  action: ProtectionAction;
}

export type FarmProtection =
  | { available: false; reason: 'not_infection_model' | 'farm_not_found' | 'no_series' | 'no_rain_recorded' }
  | {
      available: true;
      pest_model_id: number;
      pest_name: string;
      farm_id: number;
      farm_name: string;
      timezone: string;
      season_start: string;
      computed_at: string | null;
      now: number;
      match_basis: 'target_pests' | 'any_fungicide';
      default_washoff_mm: number;
      rain_last_72h_mm: number;
      rain_next_72h_mm: number;
      kickback_options: Array<{ key: string; label: string; kickback_hours: number; products: string[]; is_default?: boolean }>;
      sprays_considered: Array<{ id: number; name: string; applied_at: number; chemicals: Array<{ chemical_id: number; product_name: string; activity: DiseaseActivity }> }>;
      units: ProtectionUnit[];
    };

export const INFECTION_MODEL_TYPES: PestModelType[] = ['infection_wetness', 'generic_infection'];

export const RISK_LEVELS: RiskLevel[] = ['none', 'low', 'moderate', 'high', 'severe'];

export function riskRank(level: RiskLevel | string | null | undefined): number {
  const i = RISK_LEVELS.indexOf((level || 'none') as RiskLevel);
  return i < 0 ? 0 : i;
}
